import { Component, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of, Subject, firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CoreGridComponent } from 'src/app/shared/grid/core-grid/core-grid.component';
import { ColumnConfig } from 'src/app/shared/grid/core-grid/columnConfig.model';
import { RowActionMenuItem } from 'src/app/shared/grid/core-grid/rowActionMenuItem.model';
import { ReportTemplateListResponse } from 'src/app/models/reportTemplateListResponse.model';
import { ReportTemplateService } from 'src/app/services/reportTemplate.service';
import { ToastService } from 'src/app/services/toast.service';
import { CommonService } from 'src/app/services/common.service';
import { AuthService } from 'src/app/services/auth.service';
import { GridViewService } from 'src/app/services/grid-view.service';
import { GridSettingsService } from 'src/app/shared/grid/core-grid/grid-settings-flyout/grid-settings.service';
import { TranslateService } from 'src/app/services/translate.service';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { ICrudService } from 'src/app/shared/grid/crud.service';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { segsRouteConfig } from 'src/app/shared/constants/segsRouteConfig.constants';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { splitCamelCase } from 'src/app/_helpers/format-text';
import { escapeHtml } from 'src/app/shared/helpers/html-escape.helper';
import { ReportTemplateViewDialogComponent } from 'src/app/components/report-template-view-dialog/report-template-view-dialog.component';
import { ReportTemplateCloneDialogComponent } from 'src/app/components/report-template-clone-dialog/report-template-clone-dialog.component';
import { DeleteDialog } from 'src/app/shared/delete-dialog/delete-dialog.component';

@Component({
  standalone: false,
  selector: 'app-report-template-list',
  templateUrl: './../../shared/grid/core-grid/core-grid.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./../../shared/grid/core-grid/core-grid.component.scss', './report-template-list.component.scss']
})
export class ReportTemplateListComponent extends CoreGridComponent<ReportTemplateListResponse> {
  private readonly destroy$ = new Subject<void>();

  constructor(
    protected reportTemplateService: ReportTemplateService,
    toastService: ToastService,
    commonService: CommonService,
    dialog: MatDialog,
    authService: AuthService,
    gridViewService: GridViewService,
    gridSettingsService: GridSettingsService,
    private translateService: TranslateService,
    private organizationLevelService: OrganizationLevelService
  ) {
    super(toastService, dialog, commonService, authService, gridViewService, gridSettingsService);
    this.gridService = reportTemplateService;
    this.addDisabled = true;
    this.editDisabled = true;
    this.deleteDisabled = true;
  }

  protected get objectId(): string {
    return RolePermissionObjects.ReportTemplate;
  }

  protected override get isGridViewEnabled(): boolean {
    return true;
  }

  get hasOrgFilter(): boolean {
    return true;
  }

  override initializeProperties(): void {
    this.addEntityTitle = 'Report Template';
    this.searchPlaceholder = 'Search Templates';
    this.sortActive = nameOf<ReportTemplateListResponse>(x => x.name);
    this.defaultOrderBy = 'asc';
    this.rowActionMenuPanelClass = 'row-action-menu--compact';
  }

  override initializeColumns(): void {
    this.rowActionMenu = (row: ReportTemplateListResponse) => row.rowActions ?? this.buildRowActions(row);
    this.columnConfigs = [
      new ColumnConfig<ReportTemplateListResponse>({
        name: nameOf<ReportTemplateListResponse>(x => x.name),
        displayName: 'Name',
        cssClass: () => 'cursor-pointer',
        textCssClass: () => 'template-name-cell',
        onClick: (row) => this.openViewDialog(row),
        cellTemplate: (row) => this.buildNameCell(row)
      }),
      new ColumnConfig<ReportTemplateListResponse>({
        name: nameOf<ReportTemplateListResponse>(x => x.entityType),
        displayName: 'Type',
        isSortable: false,
        cellTemplate: (row) => splitCamelCase(row.entityType) || '-'
      }),
      new ColumnConfig<ReportTemplateListResponse>({
        name: nameOf<ReportTemplateListResponse>(x => x.createdByUserId),
        displayName: 'Created By',
        cellTemplate: (row) => row.createdByUserName || '-'
      }),
      new ColumnConfig<ReportTemplateListResponse>({
        name: nameOf<ReportTemplateListResponse>(x => x.organizationLevelId),
        displayName: 'Organization Level',
        cellTemplate: (row) => row.organizationLevelName || '-'
      }),
      new ColumnConfig<ReportTemplateListResponse>({
        name: 'action',
        displayName: 'Actions',
        isSortable: false
      })
    ];
  }

  override objectFilterMap(propertyName: string): ICrudService<unknown> | undefined {
    switch (propertyName) {
      case nameOf<ReportTemplateListResponse>(x => x.organizationLevelId): return this.organizationLevelService;
      default: return undefined;
    }
  }

  private buildNameCell(row: ReportTemplateListResponse): string {
    const icon = row.isDefault ? 'lock' : (row.isShared ? 'group' : 'person');
    const name = escapeHtml(row.name);
    let badges = '';
    if (row.isDefault) {
      badges += `<span class="template-badge template-badge--default">${escapeHtml(this.translateService.getDynamicTranslation('Default'))}</span>`;
    } else if (row.isShared && !row.canManage) {
      badges += `<span class="template-badge template-badge--readonly">${escapeHtml(this.translateService.getDynamicTranslation('Shared · read-only'))}</span>`;
    }
    return `<span class="template-name"><i class="material-icons template-name__icon">${icon}</i><span class="template-name__text">${name}</span>${badges}</span>`;
  }

  private buildRowActions(row: ReportTemplateListResponse): RowActionMenuItem<ReportTemplateListResponse>[] {
    const isLocked = row.isLocked;
    const items: RowActionMenuItem<ReportTemplateListResponse>[] = [
      {
        label: 'Rename', icon: 'edit', action: (r) => this.openRenameDialog(r),
        disabled: isLocked,
        tooltip: this.lockTooltip(row, 'Default templates cannot be renamed', 'You do not have permission to rename this template')
      },
      { label: 'Clone', icon: 'content_copy', action: (r) => this.openCloneDialog(r) },
      {
        label: 'Delete', icon: 'delete', danger: true, action: (r) => this.openDeleteTemplateDialog(r),
        disabled: isLocked,
        tooltip: this.lockTooltip(row, 'Default templates cannot be deleted', 'You do not have permission to delete this template')
      },
      {
        label: 'Share', icon: 'group_add', action: (r) => this.setShared(r, true),
        hidden: row.isShared, disabled: isLocked,
        tooltip: this.lockTooltip(row, 'Default templates cannot be modified', 'You do not have permission to modify this template')
      },
      {
        label: 'Unshare', icon: 'person', action: (r) => this.setShared(r, false),
        hidden: !row.isShared, disabled: isLocked,
        tooltip: this.lockTooltip(row, 'Default templates cannot be modified', 'You do not have permission to modify this template')
      }
    ];

    row.rowActions = items;
    return items;
  }

  private lockTooltip(row: ReportTemplateListResponse, defaultKey: string, noPermissionKey: string): string {
    if (row.isDefault) return defaultKey;
    if (!row.canManage) return noPermissionKey;
    return '';
  }

  private openViewDialog(row: ReportTemplateListResponse): void {
    this.reportTemplateService.getReportTemplateById(row.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (template) => {
        this.dialog.open(ReportTemplateViewDialogComponent, {
          data: { template },
          maxWidth: '960px',
          width: '90vw'
        });
      },
      error: (error) => this.toastService.showError(error, this.translateService.getDynamicTranslation('Failed to load templates'))
    });
  }

  private openRenameDialog(row: ReportTemplateListResponse): void {
    const dialogRef = this.dialog.open(ReportTemplateCloneDialogComponent, {
      data: { sourceName: row.name, existingNames: [], prefillSourceName: true, title: 'Rename Template', confirmLabel: 'Save' },
      width: '400px'
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe({
      next: (newName: string | null) => {
        if (newName && newName !== row.name) this.renameTemplate(row, newName);
      }
    });
  }

  private async renameTemplate(row: ReportTemplateListResponse, newName: string): Promise<void> {
    try {
      const template = await firstValueFrom(this.reportTemplateService.getReportTemplateById(row.id));
      await firstValueFrom(this.reportTemplateService.renameTemplate(template, newName));
      this.toastService.showSuccess(this.translateService.getDynamicTranslation('Template renamed successfully'));
      this.loadPage();
    } catch (error) {
      this.toastService.showError(error, this.translateService.getDynamicTranslation('Failed to rename template'));
    }
  }

  private openCloneDialog(row: ReportTemplateListResponse): void {
    const dialogRef = this.dialog.open(ReportTemplateCloneDialogComponent, {
      data: { sourceName: row.name, existingNames: [] },
      width: '400px'
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe({
      next: (chosenName: string | null) => {
        if (chosenName) this.cloneTemplate(row, chosenName, []);
      }
    });
  }

  private async cloneTemplate(row: ReportTemplateListResponse, chosenName: string, existingNames: string[]): Promise<void> {
    try {
      const template = await firstValueFrom(this.reportTemplateService.getReportTemplateById(row.id));
      await firstValueFrom(this.reportTemplateService.cloneTemplate(template, existingNames, chosenName));
      this.toastService.showSuccess(this.translateService.getDynamicTranslation('Template cloned successfully'));
      this.loadPage();
    } catch (error) {
      this.toastService.showError(error, this.translateService.getDynamicTranslation('Failed to clone template'));
    }
  }

  private openDeleteTemplateDialog(row: ReportTemplateListResponse): void {
    const dialogRef = this.dialog.open(DeleteDialog, {
      data: {
        title: this.translateService.getDynamicTranslation('Report Template'),
        service: null,
        customDelete: () => this.reportTemplateService.delete(row.id)
      },
      width: '440px',
      disableClose: true
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe({
      next: (confirmed: boolean) => {
        if (confirmed) this.loadPage();
      }
    });
  }

  private async setShared(row: ReportTemplateListResponse, isShared: boolean): Promise<void> {
    try {
      const template = await firstValueFrom(this.reportTemplateService.getReportTemplateById(row.id));
      await firstValueFrom(this.reportTemplateService.setShared(template, isShared));
      this.toastService.showSuccess(
        this.translateService.getDynamicTranslation(isShared ? 'Template shared successfully' : 'Template unshared successfully')
      );
      this.loadPage();
    } catch (error) {
      this.toastService.showError(error, this.translateService.getDynamicTranslation('Failed to update template sharing'));
    }
  }

  override initializeTabs() {
    return segsRouteConfig;
  }

  override initializeOverviewConfigs(): void {
    this.overviewConfigs = [];
  }

  override getOverviewData(): Observable<void> {
    return of(undefined);
  }

  override openEntityDialog(_entityId?: string): void { }

  override openEntityDeleteDialog(entity: ReportTemplateListResponse): void {
    this.openDeleteTemplateDialog(entity);
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.loadHeaderData();
  }

  override ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    super.ngOnDestroy();
  }
}
